<template>
  <div class="reports-analytics-main">
    <!-- Header Section -->
    <VCard class="mb-6 reports-header">
      <VCardTitle class="d-flex align-center">
        <VIcon
          icon="tabler-chart-bar"
          class="me-3"
          size="28"
          color="primary"
        />
        <div>
          <h2 class="text-h5 mb-0">
            {{ $t('history.reports.title') }}
          </h2>
          <p class="text-subtitle-2 mb-0 text-medium-emphasis">
            {{ $t('history.reports.generate_report') }}
          </p>
        </div>
      </VCardTitle>
    </VCard>

    <!-- Period Selector -->
    <VCard class="mb-6 period-selector-card">
      <VCardTitle class="text-h6">
        <VIcon
          icon="tabler-calendar"
          class="me-2"
        />
        {{ $t('history.reports.period_selector') }}
      </VCardTitle>
      <VCardText>
        <VRow class="period-selector">
          <VCol
            cols="12"
            md="4"
          >
            <VSelect
              v-model="selectedPeriod"
              :items="periodOptions"
              :label="$t('history.reports.period_selector')"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              class="period-select"
            />
          </VCol>
          
          <VCol
            v-if="selectedPeriod === 'custom'"
            cols="12"
            md="3"
          >
            <VTextField
              v-model="customDateFrom"
              :label="$t('history.filters.from_date')"
              type="date"
              density="compact"
              variant="outlined"
              class="date-from-field"
            />
          </VCol>
          
          <VCol
            v-if="selectedPeriod === 'custom'"
            cols="12"
            md="3"
          >
            <VTextField
              v-model="customDateTo"
              :label="$t('history.filters.to_date')"
              type="date"
              density="compact"
              variant="outlined"
              class="date-to-field"
            />
          </VCol>
          
          <VCol
            cols="12"
            md="2"
            class="d-flex align-center"
          >
            <VBtn
              color="primary"
              prepend-icon="tabler-refresh"
              class="refresh-data-btn"
              :loading="isRefreshing"
              @click="refreshAllData"
            >
              {{ $t('common.refresh') }}
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Reports Grid -->
    <VRow>
      <!-- Client Reports -->
      <VCol
        cols="12"
        lg="4"
      >
        <VCard class="reports-category client-reports-card h-100">
          <VCardTitle class="d-flex align-center reports-category-title">
            <VIcon
              icon="tabler-users"
              class="me-2"
              color="primary"
            />
            {{ $t('history.reports.client_reports') }}
          </VCardTitle>
          
          <VCardText>
            <VList class="reports-list">
              <VListItem
                v-for="report in clientReports"
                :key="report.key"
                class="report-item"
                @click="generateReport(report)"
              >
                <template #prepend>
                  <VAvatar
                    size="32"
                    :color="report.color"
                    variant="tonal"
                  >
                    <VIcon
                      :icon="report.icon"
                      size="18"
                    />
                  </VAvatar>
                </template>
                
                <VListItemTitle class="report-title">
                  {{ report.title }}
                </VListItemTitle>
                <VListItemSubtitle class="report-description">
                  {{ report.description }}
                </VListItemSubtitle>
                
                <template #append>
                  <VBtn
                    icon
                    variant="text"
                    size="small"
                    color="primary"
                    class="generate-report-btn"
                  >
                    <VIcon
                      icon="tabler-download"
                      size="16"
                    />
                  </VBtn>
                </template>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Container Reports -->
      <VCol
        cols="12"
        lg="4"
      >
        <VCard class="reports-category container-reports-card h-100">
          <VCardTitle class="d-flex align-center reports-category-title">
            <VIcon
              icon="tabler-truck"
              class="me-2"
              color="secondary"
            />
            {{ $t('history.reports.container_reports') }}
          </VCardTitle>
          
          <VCardText>
            <VList class="reports-list">
              <VListItem
                v-for="report in containerReports"
                :key="report.key"
                class="report-item"
                @click="generateReport(report)"
              >
                <template #prepend>
                  <VAvatar
                    size="32"
                    :color="report.color"
                    variant="tonal"
                  >
                    <VIcon
                      :icon="report.icon"
                      size="18"
                    />
                  </VAvatar>
                </template>
                
                <VListItemTitle class="report-title">
                  {{ report.title }}
                </VListItemTitle>
                <VListItemSubtitle class="report-description">
                  {{ report.description }}
                </VListItemSubtitle>
                
                <template #append>
                  <VBtn
                    icon
                    variant="text"
                    size="small"
                    color="secondary"
                    class="generate-report-btn"
                  >
                    <VIcon
                      icon="tabler-download"
                      size="16"
                    />
                  </VBtn>
                </template>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Logistics Reports -->
      <VCol
        cols="12"
        lg="4"
      >
        <VCard class="reports-category logistics-reports-card h-100">
          <VCardTitle class="d-flex align-center reports-category-title">
            <VIcon
              icon="tabler-package"
              class="me-2"
              color="success"
            />
            {{ $t('history.reports.logistics_reports') }}
          </VCardTitle>
          
          <VCardText>
            <VList class="reports-list">
              <VListItem
                v-for="report in logisticsReports"
                :key="report.key"
                class="report-item"
                @click="generateReport(report)"
              >
                <template #prepend>
                  <VAvatar
                    size="32"
                    :color="report.color"
                    variant="tonal"
                  >
                    <VIcon
                      :icon="report.icon"
                      size="18"
                    />
                  </VAvatar>
                </template>
                
                <VListItemTitle class="report-title">
                  {{ report.title }}
                </VListItemTitle>
                <VListItemSubtitle class="report-description">
                  {{ report.description }}
                </VListItemSubtitle>
                
                <template #append>
                  <VBtn
                    icon
                    variant="text"
                    size="small"
                    color="success"
                    class="generate-report-btn"
                  >
                    <VIcon
                      icon="tabler-download"
                      size="16"
                    />
                  </VBtn>
                </template>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Quick Analytics -->
    <VCard class="mt-6 quick-analytics-card">
      <VCardTitle class="d-flex align-center">
        <VIcon
          icon="tabler-chart-line"
          class="me-3"
          color="primary"
        />
        {{ $t('common.quick_analytics') }}
      </VCardTitle>
      
      <VCardText>
        <VRow v-if="!isLoadingAnalytics">
          <VCol
            cols="12"
            md="3"
          >
            <div class="text-center analytics-stat">
              <VAvatar
                size="56"
                color="primary"
                variant="tonal"
                class="mb-3"
              >
                <VIcon
                  icon="tabler-users"
                  size="28"
                />
              </VAvatar>
              <h3 class="text-h4 font-weight-bold mb-1 text-primary">
                {{ analytics.totalCustomers || 0 }}
              </h3>
              <p class="text-subtitle-2 mb-0">
                {{ $t('history.customers.title') }}
              </p>
            </div>
          </VCol>
          
          <VCol
            cols="12"
            md="3"
          >
            <div class="text-center analytics-stat">
              <VAvatar
                size="56"
                color="secondary"
                variant="tonal"
                class="mb-3"
              >
                <VIcon
                  icon="tabler-truck"
                  size="28"
                />
              </VAvatar>
              <h3 class="text-h4 font-weight-bold mb-1 text-secondary">
                {{ analytics.totalContainers || 0 }}
              </h3>
              <p class="text-subtitle-2 mb-0">
                {{ $t('history.containers.title') }}
              </p>
            </div>
          </VCol>
          
          <VCol
            cols="12"
            md="3"
          >
            <div class="text-center analytics-stat">
              <VAvatar
                size="56"
                color="success"
                variant="tonal"
                class="mb-3"
              >
                <VIcon
                  icon="tabler-weight"
                  size="28"
                />
              </VAvatar>
              <h3 class="text-h4 font-weight-bold mb-1 text-success">
                {{ formatWeight(analytics.totalWeight || 0) }}
              </h3>
              <p class="text-subtitle-2 mb-0">
                {{ $t('history.reports.weight_by_period') }}
              </p>
            </div>
          </VCol>
          
          <VCol
            cols="12"
            md="3"
          >
            <div class="text-center analytics-stat">
              <VAvatar
                size="56"
                color="warning"
                variant="tonal"
                class="mb-3"
              >
                <VIcon
                  icon="tabler-clock"
                  size="28"
                />
              </VAvatar>
              <h3 class="text-h4 font-weight-bold mb-1 text-warning">
                {{ analytics.avgProcessingTime || 0 }}
              </h3>
              <p class="text-subtitle-2 mb-0">
                {{ $t('history.containers.average_processing_time') }}
              </p>
            </div>
          </VCol>
        </VRow>
        
        <div
          v-else
          class="text-center py-8"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </div>
      </VCardText>
    </VCard>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import axios from 'axios'

definePage({
  meta: {
    requiresAuth: true,
    role: ['admin', 'manager', 'user'],
  },
})

const { t } = useI18n()
const { showSnackbar } = useSnackbar()

// Reactive data
const selectedPeriod = ref('30')
const customDateFrom = ref('')
const customDateTo = ref('')
const isRefreshing = ref(false)
const isLoadingAnalytics = ref(false)
const analytics = ref({})

// Period options
const periodOptions = computed(() => [
  { title: t('history.filters.last_30_days'), value: '30' },
  { title: t('history.filters.last_90_days'), value: '90' },
  { title: t('history.filters.last_6_months'), value: '180' },
  { title: t('history.filters.last_year'), value: '365' },
  { title: t('history.filters.custom_range'), value: 'custom' },
])

// Report configurations
const clientReports = computed(() => [
  {
    key: 'top_clients_volume',
    title: t('history.reports.top_clients_volume'),
    description: 'Export des meilleurs clients par volume',
    icon: 'tabler-trophy',
    color: 'primary',
  },
  {
    key: 'most_loyal_clients',
    title: t('history.reports.most_loyal_clients'),
    description: 'Analyse de fidélité client',
    icon: 'tabler-heart',
    color: 'error',
  },
  {
    key: 'client_satisfaction',
    title: t('history.reports.client_satisfaction'),
    description: 'Rapport de satisfaction client',
    icon: 'tabler-mood-smile',
    color: 'success',
  },
  {
    key: 'orders_evolution',
    title: t('history.reports.orders_evolution'),
    description: 'Évolution des commandes par client',
    icon: 'tabler-trending-up',
    color: 'info',
  },
])

const containerReports = computed(() => [
  {
    key: 'container_performance',
    title: t('history.reports.container_performance'),
    description: 'Performance par conteneur',
    icon: 'tabler-chart-bar',
    color: 'secondary',
  },
  {
    key: 'processing_time',
    title: t('history.reports.average_processing_time'),
    description: 'Temps de traitement moyen',
    icon: 'tabler-clock',
    color: 'warning',
  },
  {
    key: 'air_vs_sea',
    title: t('history.reports.air_vs_sea'),
    description: 'Comparaison transport aérien/maritime',
    icon: 'tabler-plane',
    color: 'info',
  },
  {
    key: 'container_efficiency',
    title: t('history.reports.container_efficiency'),
    description: 'Efficacité des conteneurs',
    icon: 'tabler-chart-pie',
    color: 'success',
  },
])

const logisticsReports = computed(() => [
  {
    key: 'weight_by_period',
    title: t('history.reports.weight_by_period'),
    description: 'Poids traité par période',
    icon: 'tabler-weight',
    color: 'success',
  },
  {
    key: 'volume_analysis',
    title: t('history.reports.volume_analysis'),
    description: 'Analyse des volumes transportés',
    icon: 'tabler-box',
    color: 'warning',
  },
  {
    key: 'capacity_forecasts',
    title: t('history.reports.capacity_forecasts'),
    description: 'Prévisions de capacité',
    icon: 'tabler-crystal-ball',
    color: 'purple',
  },
])

// Methods
const fetchAnalytics = async () => {
  try {
    isLoadingAnalytics.value = true
    
    const params = {
      period: selectedPeriod.value,
      from_date: customDateFrom.value,
      to_date: customDateTo.value,
    }
    
    const response = await axios.get('/shipment/history/analytics', { params })

    analytics.value = response.data.data
  } catch (error) {
    console.error('Error fetching analytics:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.fetch_failed', { item: 'analytics' }),
    })
  } finally {
    isLoadingAnalytics.value = false
  }
}

const refreshAllData = async () => {
  isRefreshing.value = true
  await fetchAnalytics()
  isRefreshing.value = false
}

const generateReport = async report => {
  try {
    const params = {
      period: selectedPeriod.value,
      from_date: customDateFrom.value,
      to_date: customDateTo.value,
      type: report.key,
    }
    
    const response = await axios.get('/shipment/history/reports/generate', {
      params,
      responseType: 'blob',
    })
    
    const blob = new Blob([response.data], { 
      type: response.headers['content-type'] || 'application/pdf', 
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    
    // Determine file extension based on content type
    const contentType = response.headers['content-type']
    const extension = contentType?.includes('excel') || contentType?.includes('spreadsheet') ? 'xlsx' : 'pdf'
    
    link.setAttribute('download', `${report.key}-report.${extension}`)
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
    
    showSnackbar({
      color: 'success',
      text: t('messages.report_generated'),
    })
  } catch (error) {
    console.error('Error generating report:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.report_generation_failed'),
    })
  }
}

const formatWeight = weight => {
  return `${weight.toFixed(1)} kg`
}

// Lifecycle
onMounted(() => {
  fetchAnalytics()
})
</script>

<style scoped>
.reports-analytics-main {
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

.reports-header {
  border: 1px solid rgb(var(--v-theme-surface-variant));
  background: rgb(var(--v-theme-surface));
}

.period-selector-card {
  border: 1px solid rgb(var(--v-theme-primary));
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.period-selector {
  border-radius: 8px;
  padding: 16px;
  margin: -8px;
}

.reports-category {
  transition: transform 0.2s ease-in-out;
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

.reports-category:hover {
  transform: translateY(-2px);
}

.client-reports-card {
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.container-reports-card {
  border-left: 4px solid rgb(var(--v-theme-secondary));
}

.logistics-reports-card {
  border-left: 4px solid rgb(var(--v-theme-success));
}

.report-item {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  border-radius: 8px;
  margin-bottom: 8px;
}

.reports-list {
  padding: 0;
}

.analytics-stat {
  padding: 24px 16px;
  border-radius: 12px;
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

.quick-analytics-card {
  border: 1px solid rgb(var(--v-theme-surface-variant));
  background: rgb(var(--v-theme-surface));
}

.generate-report-btn {
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}

.report-item:hover .generate-report-btn {
  opacity: 1;
}
</style>